import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { Loader2, Plus, X } from "lucide-react"
import { useState } from "react"
import { toastApiError } from "../../api/apiError"
import {
  useCreateCustomer,
  type CustomerOut,
} from "../../api/customerRequestsApi"
import { useCustomerDetail } from "../../api/customersApi"

/** The existing customer's id from a 409 DUPLICATE body, or null. Error responses
 *  skip the camelCase interceptor, so the raw wire key is read directly. */
function duplicateCustomerId(error: unknown): string | null {
  const detail = (
    error as {
      response?: { data?: { detail?: { code?: string; customer_id?: string } } }
    }
  )?.response?.data?.detail
  return detail?.code === "DUPLICATE" ? (detail.customer_id ?? null) : null
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const RESPONSE_FORMATS = ["excel", "pdf", "portal"] as const
const FIELD_CLASS = "border-grey4 bg-grey2 text-white"

interface AttributeRow {
  id: string
  key: string
  value: string
}

function FieldLabel({
  htmlFor,
  children,
}: {
  htmlFor?: string
  children: React.ReactNode
}) {
  return (
    <label htmlFor={htmlFor} className="text-[11.5px] font-semibold text-grey5">
      {children}
    </label>
  )
}

interface CreateCustomerModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialName?: string
  onCreated: (customer: CustomerOut) => void
}

export function CreateCustomerModal({
  open,
  onOpenChange,
  initialName = "",
  onCreated,
}: CreateCustomerModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <CreateCustomerForm
          initialName={initialName}
          onCreated={onCreated}
          onOpenChange={onOpenChange}
        />
      </DialogContent>
    </Dialog>
  )
}

// Hooks live here so they mount/reset with the dialog (DialogContent unmounts
// on close).
function CreateCustomerForm({
  initialName,
  onCreated,
  onOpenChange,
}: {
  initialName: string
  onCreated: (customer: CustomerOut) => void
  onOpenChange: (open: boolean) => void
}) {
  const { translate } = useTranslation()
  const { mutate: createCustomer, isPending } = useCreateCustomer()

  const [name, setName] = useState(initialName)
  // Set when the create is refused because the name is taken: the 409 names the
  // row, so the modal can hand the user that customer instead of a dead end.
  const [duplicateId, setDuplicateId] = useState<string | null>(null)
  // Fetched only once a duplicate is reported (`enabled: !!id`), so the normal
  // create path costs nothing.
  const duplicate = useCustomerDetail(duplicateId)
  const [email, setEmail] = useState("")
  const [declarationFreshnessDays, setDeclarationFreshnessDays] = useState("")
  const [concentrationThresholdPct, setConcentrationThresholdPct] = useState("")
  const [preferredResponseFormat, setPreferredResponseFormat] = useState<
    string | null
  >(null)
  const [attributes, setAttributes] = useState<AttributeRow[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})

  function validate(): boolean {
    const next: Record<string, string> = {}

    if (!name.trim()) {
      next.name = translate("MC_Q_NEW_CUSTOMER_ERR_NAME_REQUIRED")
    }

    if (email.trim() && !EMAIL_REGEX.test(email.trim())) {
      next.email = translate("MC_Q_NEW_CUSTOMER_ERR_EMAIL_INVALID")
    }

    if (declarationFreshnessDays.trim()) {
      const n = Number(declarationFreshnessDays)
      if (!Number.isInteger(n) || n <= 0) {
        next.declarationFreshnessDays = translate(
          "MC_Q_NEW_CUSTOMER_ERR_FRESHNESS_INVALID",
        )
      }
    }

    if (concentrationThresholdPct.trim()) {
      const n = Number(concentrationThresholdPct)
      if (Number.isNaN(n) || n < 0 || n > 100) {
        next.concentrationThresholdPct = translate(
          "MC_Q_NEW_CUSTOMER_ERR_THRESHOLD_INVALID",
        )
      }
    }

    setErrors(next)
    return Object.keys(next).length === 0
  }

  function handleAddAttributeRow() {
    setAttributes((rows) => [
      ...rows,
      { id: crypto.randomUUID(), key: "", value: "" },
    ])
  }

  function handleRemoveAttributeRow(id: string) {
    setAttributes((rows) => rows.filter((row) => row.id !== id))
  }

  function handleAttributeChange(
    id: string,
    field: "key" | "value",
    value: string,
  ) {
    setAttributes((rows) =>
      rows.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    )
  }

  function handleSave() {
    if (!validate()) return

    const additionalAttributes = attributes.reduce<Record<string, string>>(
      (acc, { key, value }) => {
        if (key.trim()) acc[key.trim()] = value
        return acc
      },
      {},
    )

    createCustomer(
      {
        name: name.trim(),
        email: email.trim() || null,
        declarationFreshnessDays: declarationFreshnessDays.trim()
          ? Number(declarationFreshnessDays)
          : null,
        concentrationThresholdPct: concentrationThresholdPct.trim()
          ? Number(concentrationThresholdPct)
          : null,
        preferredResponseFormat,
        additionalAttributes: Object.keys(additionalAttributes).length
          ? additionalAttributes
          : null,
      },
      {
        onSuccess: (customer) => {
          onCreated(customer)
          onOpenChange(false)
        },
        onError: (err) => {
          // A duplicate name is not a failure the user can only retry out of —
          // they wanted this customer, and it exists. Offer it (A7: the create
          // 409 fired twice in two seconds during the live test).
          const existingId = duplicateCustomerId(err)
          if (existingId) {
            setDuplicateId(existingId)
            return
          }
          toastApiError(err, translate, "MC_API_ERROR_GENERIC")
        },
      },
    )
  }

  const duplicateNotice = duplicateId ? (
    <div
      role="alert"
      className="rounded-md border border-yellow1/30 bg-yellow1/10 p-2.5 text-[12px] text-white"
    >
      {translate("MC_Q_NEW_CUSTOMER_DUPLICATE")}
      <button
        type="button"
        className="mt-1.5 block font-medium text-yellow1 hover:underline disabled:opacity-50"
        disabled={!duplicate.data}
        onClick={() => {
          if (!duplicate.data) return
          onCreated(duplicate.data)
          onOpenChange(false)
        }}
      >
        {translate("MC_Q_NEW_CUSTOMER_USE_EXISTING")}
      </button>
    </div>
  ) : null

  return (
    <>
      <DialogHeader>
        <DialogTitle>{translate("MC_Q_NEW_CUSTOMER_TITLE")}</DialogTitle>
        <DialogDescription>
          {translate("MC_Q_NEW_CUSTOMER_DESC")}
        </DialogDescription>
      </DialogHeader>

      <div className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto p-1">
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor="new-customer-name">
            {translate("MC_Q_NEW_CUSTOMER_NAME_LABEL")}
          </FieldLabel>
          <Input
            id="new-customer-name"
            autoFocus
            value={name}
            placeholder={translate("MC_Q_NEW_CUSTOMER_NAME_PLACEHOLDER")}
            aria-invalid={!!errors.name}
            onChange={(e) => {
              setName(e.target.value)
              if (errors.name) setErrors((p) => ({ ...p, name: "" }))
            }}
            className={FIELD_CLASS}
          />
          {errors.name && (
            <span role="alert" className="text-[11px] text-red1">
              {errors.name}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor="new-customer-email">
            {translate("MC_Q_NEW_CUSTOMER_EMAIL_LABEL")}
          </FieldLabel>
          <Input
            id="new-customer-email"
            value={email}
            placeholder={translate("MC_Q_NEW_CUSTOMER_EMAIL_PLACEHOLDER")}
            aria-invalid={!!errors.email}
            onChange={(e) => {
              setEmail(e.target.value)
              if (errors.email) setErrors((p) => ({ ...p, email: "" }))
            }}
            className={FIELD_CLASS}
          />
          {errors.email && (
            <span role="alert" className="text-[11px] text-red1">
              {errors.email}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="new-customer-freshness">
              {translate("MC_Q_NEW_CUSTOMER_FRESHNESS_LABEL")}
            </FieldLabel>
            <Input
              id="new-customer-freshness"
              type="number"
              min={1}
              step={1}
              value={declarationFreshnessDays}
              placeholder={translate("MC_Q_NEW_CUSTOMER_FRESHNESS_PLACEHOLDER")}
              aria-invalid={!!errors.declarationFreshnessDays}
              onChange={(e) => {
                setDeclarationFreshnessDays(e.target.value)
                if (errors.declarationFreshnessDays)
                  setErrors((p) => ({ ...p, declarationFreshnessDays: "" }))
              }}
              className={FIELD_CLASS}
            />
            {errors.declarationFreshnessDays && (
              <span role="alert" className="text-[11px] text-red1">
                {errors.declarationFreshnessDays}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="new-customer-threshold">
              {translate("MC_Q_NEW_CUSTOMER_THRESHOLD_LABEL")}
            </FieldLabel>
            <Input
              id="new-customer-threshold"
              type="number"
              min={0}
              max={100}
              step="any"
              value={concentrationThresholdPct}
              placeholder={translate("MC_Q_NEW_CUSTOMER_THRESHOLD_PLACEHOLDER")}
              aria-invalid={!!errors.concentrationThresholdPct}
              onChange={(e) => {
                setConcentrationThresholdPct(e.target.value)
                if (errors.concentrationThresholdPct)
                  setErrors((p) => ({
                    ...p,
                    concentrationThresholdPct: "",
                  }))
              }}
              className={FIELD_CLASS}
            />
            {errors.concentrationThresholdPct && (
              <span role="alert" className="text-[11px] text-red1">
                {errors.concentrationThresholdPct}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <FieldLabel>{translate("MC_Q_NEW_CUSTOMER_FORMAT_LABEL")}</FieldLabel>
          <Select
            value={preferredResponseFormat ?? undefined}
            onValueChange={(v) => setPreferredResponseFormat(v)}
          >
            <SelectTrigger className={FIELD_CLASS}>
              <SelectValue
                placeholder={translate("MC_Q_NEW_CUSTOMER_FORMAT_PLACEHOLDER")}
              />
            </SelectTrigger>
            <SelectContent>
              {RESPONSE_FORMATS.map((format) => (
                <SelectItem key={format} value={format}>
                  {translate(
                    `MC_Q_NEW_CUSTOMER_FORMAT_${format.toUpperCase()}` as
                      | "MC_Q_NEW_CUSTOMER_FORMAT_EXCEL"
                      | "MC_Q_NEW_CUSTOMER_FORMAT_PDF"
                      | "MC_Q_NEW_CUSTOMER_FORMAT_PORTAL",
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <FieldLabel>
              {translate("MC_Q_NEW_CUSTOMER_ATTRS_LABEL")}
            </FieldLabel>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-grey5 hover:text-white"
              onClick={handleAddAttributeRow}
            >
              <Plus size={12} className="mr-1" />
              {translate("MC_Q_NEW_CUSTOMER_ATTRS_ADD_ROW")}
            </Button>
          </div>
          {attributes.length === 0 ? (
            <span className="text-[11px] text-grey5">
              {translate("MC_Q_NEW_CUSTOMER_ATTRS_EMPTY")}
            </span>
          ) : (
            <div className="flex flex-col gap-2">
              {attributes.map((row) => (
                <div key={row.id} className="flex items-center gap-2">
                  <Input
                    value={row.key}
                    placeholder={translate(
                      "MC_Q_NEW_CUSTOMER_ATTRS_KEY_PLACEHOLDER",
                    )}
                    onChange={(e) =>
                      handleAttributeChange(row.id, "key", e.target.value)
                    }
                    className={cn(FIELD_CLASS, "flex-1")}
                  />
                  <Input
                    value={row.value}
                    placeholder={translate(
                      "MC_Q_NEW_CUSTOMER_ATTRS_VALUE_PLACEHOLDER",
                    )}
                    onChange={(e) =>
                      handleAttributeChange(row.id, "value", e.target.value)
                    }
                    className={cn(FIELD_CLASS, "flex-1")}
                  />
                  <button
                    type="button"
                    aria-label={translate("MC_Q_NEW_CUSTOMER_ATTRS_REMOVE_ROW")}
                    onClick={() => handleRemoveAttributeRow(row.id)}
                    className="shrink-0 text-grey5 hover:text-red1"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {duplicateNotice}

      <DialogFooter>
        <Button
          variant="outline"
          onClick={() => onOpenChange(false)}
          disabled={isPending}
        >
          {translate("MC_Q_NEW_CUSTOMER_BTN_CANCEL")}
        </Button>
        <Button
          className="bg-yellow1 text-black hover:bg-yellow2"
          onClick={handleSave}
          disabled={isPending}
        >
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {translate("MC_Q_NEW_CUSTOMER_BTN_SAVE")}
        </Button>
      </DialogFooter>
    </>
  )
}
